import { useLayoutEffect, useRef } from 'react';

/**
 * Places a shared highlight under whichever child of the returned ref carries `data-current`, so
 * moving between destinations slides one pill instead of switching one off and another on.
 *
 * The geometry goes on the container as CSS variables (`--ind-x`, `--ind-y`, `--ind-w`,
 * `--ind-h`); the stylesheet draws the pill and decides whether it moves. `data-indicator-ready`
 * is set one frame after the first placement, so the pill appears in place on load and only
 * animates on a later change. A resize (a font arriving, a label wrapping) re-measures.
 */
export function useSlidingIndicator<T extends HTMLElement>(key: unknown) {
  const ref = useRef<T | null>(null);

  useLayoutEffect(() => {
    const box = ref.current;
    if (box === null) {
      return undefined;
    }

    const place = () => {
      const active = box.querySelector<HTMLElement>('[data-current]');
      if (active === null) {
        box.removeAttribute('data-has-indicator');
        return;
      }
      box.style.setProperty('--ind-x', `${String(active.offsetLeft)}px`);
      box.style.setProperty('--ind-y', `${String(active.offsetTop)}px`);
      box.style.setProperty('--ind-w', `${String(active.offsetWidth)}px`);
      box.style.setProperty('--ind-h', `${String(active.offsetHeight)}px`);
      box.setAttribute('data-has-indicator', '');
    };

    place();
    const frame = requestAnimationFrame(() => {
      box.setAttribute('data-indicator-ready', '');
    });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    observer?.observe(box);

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [key]);

  return ref;
}
